import React from "react";
import { Navbar } from "../components/app/Navbar";
import { Footer } from "../components/app/Footer";
import { useT } from "../context/LanguageContext";

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-24">
    <h2 className="font-display text-xl font-semibold text-gray-900">{title}</h2>
    <div className="mt-3 space-y-3 text-gray-700 leading-relaxed text-[15px]">{children}</div>
  </section>
);

export default function Terms() {
  const { t, lang } = useT();
  const locale = lang === "es" ? "es-ES" : "en-US";
  const lastUpdated = new Date().toLocaleDateString(locale, {
    year: "numeric", month: "long", day: "numeric",
  });

  return (
    <div className="min-h-screen flex flex-col bg-[#f9f9f8]" data-testid="terms-page">
      <Navbar />
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-green-800">{t("legal.eyebrow")}</p>
        <h1 className="font-display mt-2 text-3xl sm:text-4xl font-semibold text-gray-900" data-testid="terms-title">
          {t("terms.title")}
        </h1>
        <p className="mt-2 text-sm text-gray-500">
          {t("legal.lastUpdated")} {lastUpdated}
        </p>

        <p className="mt-6 text-gray-600 leading-relaxed text-[15px]">
          {t("terms.intro")}
        </p>

        <div className="mt-10 space-y-10">
          <Section id="subscription" title={t("terms.subTitle")}>
            <p>{t("terms.subBody1")}</p>
            <p>{t("terms.subBody2")}</p>
            <p>{t("terms.subBody3")}</p>
          </Section>

          <Section id="refunds" title={t("terms.refundsTitle")}>
            <p className="font-semibold text-gray-900" data-testid="terms-no-refunds">
              {t("terms.refundsHeadline")}
            </p>
            <p>{t("terms.refundsBody")}</p>
          </Section>

          <Section id="no-guarantee" title={t("terms.guaranteeTitle")}>
            <p data-testid="terms-no-guarantee">{t("terms.guaranteeBody1")}</p>
            <p>{t("terms.guaranteeBody2")}</p>
          </Section>

          <Section id="responsibility" title={t("terms.respTitle")}>
            <p data-testid="terms-user-responsibility">{t("terms.respBody1")}</p>
            <p>{t("terms.respBody2")}</p>
          </Section>

          <Section id="sms" title={t("terms.smsTitle")}>
            <p>{t("terms.smsBody1")}</p>
            <p>{t("terms.smsBody2")}</p>
            <p>
              {t("terms.smsBody3Pre")}{" "}
              <span className="font-semibold text-gray-900">HELP</span>.{" "}
              {t("terms.smsBody3Mid")}{" "}
              <span className="font-semibold text-gray-900">STOP</span>.
            </p>
            {/* A2P 10DLC compliance: explicit "not a condition of
                purchase" language directly inside the SMS section so the
                consent disclaimer travels with the rest of the SMS
                terms. */}
            <p data-testid="terms-sms-not-required-1">{t("terms.smsBodyNotRequired1")}</p>
            <p data-testid="terms-sms-not-required-2">{t("terms.smsBodyNotRequired2")}</p>
            <p data-testid="terms-sms-phone-optional">{t("terms.smsPhoneOptional")}</p>
            <p data-testid="terms-sms-not-required-3">{t("terms.smsBodyNotRequired3")}</p>
            <p data-testid="terms-sms-mobile-not-shared">{t("terms.smsMobileNotShared")}</p>

            {/* SMS Program URLs — mirrors the same cross-reference block
                in /privacy so Twilio A2P 10DLC reviewers can trace the
                three public documents from any entry point. */}
            <h3
              className="font-display text-base sm:text-lg font-semibold text-gray-900 tracking-tight pt-2"
              data-testid="terms-sms-program-urls-title"
            >
              {t("terms.smsProgramUrlsTitle")}
            </h3>
            <ul className="space-y-2 text-gray-600">
              <li data-testid="terms-sms-program-url-details">
                <span className="font-medium text-gray-900">{t("terms.smsProgramDetailsLabel")}</span>{" "}
                <a
                  href="https://cleaning.tasklio.co/sms-opt-in"
                  className="text-green-800 hover:underline break-all"
                >
                  https://cleaning.tasklio.co/sms-opt-in
                </a>
              </li>
              <li data-testid="terms-sms-program-url-privacy">
                <span className="font-medium text-gray-900">{t("terms.smsPolicyLinkLabel")}</span>{" "}
                <a
                  href="https://cleaning.tasklio.co/privacy"
                  className="text-green-800 hover:underline break-all"
                >
                  https://cleaning.tasklio.co/privacy
                </a>
              </li>
              <li data-testid="terms-sms-program-url-terms">
                <span className="font-medium text-gray-900">{t("terms.smsTermsLinkLabel")}</span>{" "}
                <a
                  href="https://cleaning.tasklio.co/terms"
                  className="text-green-800 hover:underline break-all"
                >
                  https://cleaning.tasklio.co/terms
                </a>
              </li>
            </ul>
          </Section>

          <Section id="sms-terms" title={t("terms.smsConsentTitle")}>
            <p data-testid="terms-sms-consent">{t("terms.smsConsentBody")}</p>
          </Section>

          <Section id="changes" title={t("terms.changesTitle")}>
            <p>{t("terms.changesBody")}</p>
          </Section>

          <Section id="contact" title={t("terms.contactTitle")}>
            <p>
              {t("terms.contactBody")}{" "}
              <a
                href="mailto:support@tasklio.co"
                className="text-green-800 hover:underline font-medium"
                data-testid="terms-contact-link"
              >
                support@tasklio.co
              </a>
              .
            </p>
          </Section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
